import { useErrorInlineNotification } from '@/components/ErrorInlineNotification';
import { SuspenseErrorBoundary } from '@/components/SuspenseErrorBoundary';
import { useGetOfficeWithdrawalQuestions } from '@/context/services/office_withdrawal/OfficeWithdrawal.service';
import {
  OfficeWithdrawalQuestion,
  OfficeWithdrawalRequestWithdrawalForm,
} from '@/context/services/office_withdrawal/type';
import { WithdrawalButton } from '@/features/office_withdrawal/components/WithdrawalButton/WithdrawalButton';
import { WithdrawalLayout } from '@/features/office_withdrawal/components/WithdrawalLayout/WithdrawalLayout';
import { useSubmitOfficeWithdrawal } from '@/features/office_withdrawal/hooks/useSubmitOfficeWithdrawal';
import { officeWithdrawalDefaultValues } from '@/features/office_withdrawal/type';
import { Loading } from '@moneyforward/ap-frontend-components';
import { Divider } from 'antd';
import classNames from 'classnames';
import { memo, Suspense, useCallback, useState } from 'react';
import {
  Form,
  useForm,
  UseFormReturn,
  useWatch,
  type FormSubmitHandler,
} from 'react-hook-form';
import { ConfirmationSection } from './components/ConfirmationSection';
import { ReasonSection } from './components/ReasonSection';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

type Props = {
  methods: UseFormReturn<OfficeWithdrawalRequestWithdrawalForm>;
  setInlineError: (error: Error) => void;
};

type OfficeWithdrawalLoadingProps = {
  methods: UseFormReturn<OfficeWithdrawalRequestWithdrawalForm>;
};

export const InnerOfficeWithdrawal = memo(
  ({ methods, setInlineError }: Props) => {
    const questions = useGetOfficeWithdrawalQuestions();
    const [isLoading, setIsLoading] = useState(false);
    const setLoading = useCallback((isLoading: boolean) => {
      setIsLoading(isLoading);
    }, []);

    const { onSubmitWithdrawal } = useSubmitOfficeWithdrawal({
      setLoading,
      setInlineError,
    });

    const { control, resetField } = methods;
    const confirmed = useWatch({ control, name: 'confirmed' });

    const onSubmit = useCallback<
      FormSubmitHandler<OfficeWithdrawalRequestWithdrawalForm>
    >(
      async ({ data }) => {
        await onSubmitWithdrawal(data);
      },
      [onSubmitWithdrawal]
    );

    return (
      <>
        <Form<OfficeWithdrawalRequestWithdrawalForm>
          control={control}
          onSubmit={onSubmit}
        >
          <ConfirmationSection control={control} />
          <Divider />
          <ReasonSection
            control={control}
            resetField={resetField}
            questions={questions}
            isLoading={isLoading}
          />
          <WithdrawalButton isDisabled={!confirmed} />
        </Form>
        {isLoading && <Loading isFullFlame size='large' isOverlay />}
      </>
    );
  }
);

InnerOfficeWithdrawal.displayName = 'InnerOfficeWithdrawal';

export const OfficeWithdrawalLoading = memo(
  ({ methods }: OfficeWithdrawalLoadingProps) => {
    const { control, resetField } = methods;
    const questions: OfficeWithdrawalQuestion[] = [];

    return (
      <>
        <ConfirmationSection control={control} />
        <Divider />
        <ReasonSection
          control={control}
          resetField={resetField}
          questions={questions}
          isLoading
        />
        <WithdrawalButton isLoading />
      </>
    );
  }
);

OfficeWithdrawalLoading.displayName = 'OfficeWithdrawalLoading';

export const OfficeWithdrawal = memo(() => {
  const methods = useForm<OfficeWithdrawalRequestWithdrawalForm>({
    defaultValues: officeWithdrawalDefaultValues,
  });

  const [
    { setError: setInlineError },
    ErrorContextHolder,
    ErrorInlineNotificationProvider,
  ] = useErrorInlineNotification();

  return (
    <ErrorInlineNotificationProvider value={setInlineError}>
      {ErrorContextHolder.props.message && (
        <div className={cx(styles['error-inline-notification-wrap'])}>
          {ErrorContextHolder}
        </div>
      )}
      <WithdrawalLayout>
        <SuspenseErrorBoundary>
          <Suspense fallback={<OfficeWithdrawalLoading methods={methods} />}>
            <InnerOfficeWithdrawal
              methods={methods}
              setInlineError={setInlineError}
            />
          </Suspense>
        </SuspenseErrorBoundary>
      </WithdrawalLayout>
    </ErrorInlineNotificationProvider>
  );
});

OfficeWithdrawal.displayName = 'OfficeWithdrawal';
